<template>
  <article>
    <activity-stage :customRootContainerClass="'paragraph-centered'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <figure v-if="currentStep?.answerImage?.image?.src">
          <img
            alt="Background image"
            style="height: 100%; width: auto"
            :src="currentStep?.answerImage?.image?.src"
          />
        </figure>
        <div
          class="text-container"
          v-for="(step, stepIndex) in currentRound?.steps"
          :key="stepIndex"
          :class="getTextClasses"
        >
          <blinking-cursor-text :text="step.answer" :text-step="stepIndex"></blinking-cursor-text>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'Center of Attention'
    };
  },

  computed: {
    getTextClasses() {
      const textLength = this.currentRound.steps[0].answer.length;

      if (textLength > 270 && textLength < 310) {
        return 'longText';
      }

      if (textLength >= 310) {
        return 'extraLongText';
      }

      return '';
    }
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      this.createRounds();

      await this.loadImages();
    },

    async loadImages() {
      const { rounds } = this.activityData;

      rounds.forEach(({ steps }) => {
        steps.forEach(step => this.queueImage(step.answerImage?.image?.src));
      });

      await this.downloadImages();
    },

    getKeyboardConfig() {
      return {
        hideNumberRow: !this.activityData.keyboard.showNumbers
      };
    },

    startActivity() {
      this.setupNewRound();
      this.errorLoggerStart();

      this.$nextTick(() => {
        this.setActivityContainerHeight();
        this.setBackgroundImageHeight();
      });

      TweenMax.to('.activity-stage', 0.5, {
        autoAlpha: 1,
        onComplete: this.enableKeyboard
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.finishStep();
      }
    },

    finishStep() {
      this.hideKeyboard();
      this.completeStep();
      this.completeActivity();
    },

    setBackgroundImageHeight() {
      const activityStageEl = document.querySelector('.activity-stage');
      const backgroundImageEl = document.querySelector('.activity-stage img');

      if (activityStageEl && backgroundImageEl) {
        // 61 = height of instructions container (45px) + 16px of extra space
        backgroundImageEl.style.maxHeight = `${activityStageEl.clientHeight - 61}px`;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.paragraph-centered {
  .text-container {
    background-color: white;
    margin: auto;
    position: absolute;
    width: 50%;
    top: 60%;
    left: 0;
    right: 0;

    &.extraLongText {
      width: 70%;
      top: 60%;
    }

    &.longText {
      width: 65%;
      margin: 0 21%;
    }

    p {
      font-size: 18px;
    }
  }
}

#scroll-container {
  height: 100%;
  overflow: scroll;
  position: relative;
}

figure {
  margin: 0px;
  text-align: center;

  img {
    height: auto;
    width: 85%;
  }
}

.chrome-os {
  figure {
    img {
      width: 70%;
    }
  }
}

.tablet {
  .paragraph-centered {
    img {
      display: block;
      margin: auto;
    }

    .text-container {
      bottom: 0px;
      margin: 0 auto;
      top: unset;
      width: 45%;

      &.extraLongText {
        width: 60%;
      }

      &.longText {
        margin: 0 24%;
        width: 60%;
      }
    }
  }
}
</style>
